import axios, {AxiosInstance, AxiosRequestConfig, AxiosError} from 'axios';
import {config} from '../config/config';

class ApiService {
    private api: AxiosInstance;
    private baseURL = config.apiUrl;
    private maxRetries = 3;
    private retryDelay = 1000; // 1 second
    private isRefreshing = false;
    private refreshSubscribers: ((token: string) => void)[] = [];
    private onTokenRefreshed: ((token: string) => void) | null = null;
    private onAuthFailure: (() => void) | null = null;

    constructor() {
        this.api = axios.create({
            baseURL: this.baseURL,
            headers: {
                'Content-Type': 'application/json',
            },
            withCredentials: true, // Include cookies for all requests
        });

        // Request interceptor to add auth token
        this.api.interceptors.request.use(
            (config) => {
                const token = localStorage.getItem('access_token');
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`;
                }
                return config;
            },
            (error) => Promise.reject(error)
        );

        // Response interceptor to handle token refresh and network errors
        this.api.interceptors.response.use(
            (response) => response,
            async (error: AxiosError) => {
                const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean; _retryCount?: number };

                if (!originalRequest) {
                    return Promise.reject(error);
                }

                // Retry on network errors (backend not ready yet)
                const isNetworkError = !error.response && (error.code === 'ERR_NETWORK' || error.code === 'ECONNREFUSED');
                const retryCount = originalRequest._retryCount || 0;

                if (isNetworkError && retryCount < this.maxRetries) {
                    originalRequest._retryCount = retryCount + 1;

                    // Wait before retrying with exponential backoff
                    await new Promise(resolve => setTimeout(resolve, this.retryDelay * (retryCount + 1)));

                    return this.api(originalRequest);
                }

                if (error.response?.status === 401 && !originalRequest._retry) {
                    originalRequest._retry = true;

                    // If already refreshing, queue this request to retry after refresh completes
                    if (this.isRefreshing) {
                        return new Promise((resolve) => {
                            this.refreshSubscribers.push((token: string) => {
                                if (originalRequest.headers) {
                                    originalRequest.headers.Authorization = `Bearer ${token}`;
                                }
                                resolve(this.api(originalRequest));
                            });
                        });
                    }

                    this.isRefreshing = true;

                    try {
                        const response = await this.refreshToken();
                        const {access_token} = response.data;

                        localStorage.setItem('access_token', access_token);

                        // Notify React context of token refresh
                        if (this.onTokenRefreshed) {
                            this.onTokenRefreshed(access_token);
                        }

                        // Retry all queued requests with new token
                        this.refreshSubscribers.forEach((callback) => callback(access_token));
                        this.refreshSubscribers = [];

                        if (originalRequest.headers) {
                            originalRequest.headers.Authorization = `Bearer ${access_token}`;
                        }

                        return this.api(originalRequest);
                    } catch (refreshError) {
                        // Refresh failed, notify auth context and clear state
                        localStorage.removeItem('access_token');
                        this.refreshSubscribers = [];

                        if (this.onAuthFailure) {
                            this.onAuthFailure();
                        } else {
                            // Fallback if no handler set
                            window.location.href = '/login';
                        }
                    } finally {
                        this.isRefreshing = false;
                    }
                }

                return Promise.reject(error);
            }
        );
    }

    private async refreshToken() {
        return axios.post(`${this.baseURL}/api/v1/auth/refresh`, {}, {
            withCredentials: true, // Include cookies for refresh token
        });
    }

    async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
        const response = await this.api.get(url, config);
        return response.data;
    }

    async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
        const response = await this.api.post(url, data, config);
        return response.data;
    }

    async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
        const response = await this.api.put(url, data, config);
        return response.data;
    }

    async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
        const response = await this.api.patch(url, data, config);
        return response.data;
    }

    async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
        const response = await this.api.delete(url, config);
        return response.data;
    }

    // Allow auth context to subscribe to token refresh events
    setOnTokenRefreshed(callback: (token: string) => void) {
        this.onTokenRefreshed = callback;
    }

    // Allow auth context to handle auth failures gracefully
    setOnAuthFailure(callback: () => void) {
        this.onAuthFailure = callback;
    }
}

export const apiService = new ApiService();
